<template>
  <v-list-item
    class="pa-4 ma-0 rounded"
    color="blue lighten-1"
    @click="onClick(notification)"
  >
    <v-list-item-icon class="ma-0 pa-0 mt-1 me-4">
      <v-avatar size="32" :color="badgeStyle.color">
        <v-icon color="white" :size="size">
          {{ badgeStyle.icon }}
        </v-icon>
      </v-avatar>
    </v-list-item-icon>

    <v-list-item-content class="ma-0 pa-0">
      <p
        class="ma-0 pa-0 text-body-2 blue-grey--text"
        :class="notification.read ? 'text--darken-1' : 'text--darken-3'"
      >
        {{ message }}
      </p>
      <p
        v-if="notification.type === 'shareRev' && comment"
        class="caption grey--text text--darken-2 ma-0 pa-0 mt-1"
      >
        <span class="font-weight-bold">
          {{ $t('common.comment_one') + ': ' }}
        </span>
        {{ comment }}
      </p>
      <p class="caption grey--text text--darken-2 ma-0 pa-0 mt-2">{{ date }}</p>
    </v-list-item-content>
    <v-badge
      v-if="!notification.read"
      dot
      color="primary"
      class="mx-4 pa-0 my-0"
    />
  </v-list-item>
</template>

<script>
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import he from 'he'

import { NOTIFICATION_TYPES } from './constants'

export default {
  props: ['notification'],

  mixins: [mixin],

  computed: {
    size() {
      return this.badgeStyle.size ?? 16
    },

    date() {
      return utils.dates.humanDate(this.notification.createdAt)
    },

    message() {
      return he.decode(this.notification?.message ?? '')
    },

    comment() {
      return this.notification?.metadata?.message ?? ''
    },

    taskStatusColor() {
      switch (this.notification?.metadata?.status) {
        case 'open':
          return 'teal'
        case 'inProgress':
          return 'blue'
        case 'completed':
          return 'green'
        default:
          return 'blue'
      }
    },

    badgeStyle() {
      switch (this.notification.type) {
        case NOTIFICATION_TYPES.COMMENT_CREATED:
          return {
            color: this.notification?.metadata?.hasMentions ? 'orange' : 'blue',
            icon: 'mdi-comment-outline',
          }
        case NOTIFICATION_TYPES.ADD_NEW_COLLABORATOR:
          return {
            color: 'yellow',
            size: 20,
            icon: 'mdi-bee',
          }
        case NOTIFICATION_TYPES.RECOGNITION_LIKED:
        case NOTIFICATION_TYPES.REV_LIKED:
          return {
            color: 'blue darken-1',
            icon: 'mdi-thumb-up-outline',
          }
        case NOTIFICATION_TYPES.SHARE_REV:
          return {
            color: 'purple',
            icon: 'mdi-share-variant',
          }
        case NOTIFICATION_TYPES.INACTIVE_USERS_IN_REV_REMINDER:
          return {
            color: 'blue-grey darken-1',
            icon: 'mdi-ghost-outline',
          }
        case NOTIFICATION_TYPES.REV_DELETED:
          return {
            color: 'red',
            icon: 'mdi-delete-outline',
          }
        case NOTIFICATION_TYPES.ADD_NEW_COACH:
          return {
            color: 'orange',
            icon: 'mdi-account-star-outline',
          }
        case NOTIFICATION_TYPES.ADD_ROOT_CAUSE_ASSIGNEE:
          return {
            color: 'blue lighten-1',
            icon: 'mdi-sticker-check-outline',
          }
        case NOTIFICATION_TYPES.ADD_FINDING_ASSIGNEE:
          return {
            color: 'teal',
            icon: 'mdi-toy-brick-search-outline',
          }
        case NOTIFICATION_TYPES.CHALLENGE_CREATED:
          return {
            color: 'orange darken-1',
            icon: 'mdi-target',
          }
        case NOTIFICATION_TYPES.REV_IMPLEMENTED:
          return {
            color: 'green',
            icon: 'mdi-check-outline',
          }
        case NOTIFICATION_TYPES.REV_COMPLETED:
          return {
            color: 'green',
            icon: 'mdi-check-outline-circle-outline',
          }
        case NOTIFICATION_TYPES.REV_MARKED_NOT_VALID:
          return {
            color: 'purple darken-1',
            icon: 'mdi-lightbulb-off-outline',
          }
        case NOTIFICATION_TYPES.REV_FINANCIALLY_VALIDATED:
          return {
            color: 'green lighten-1',
            icon: 'mdi-check-decagram-outline',
          }
        case NOTIFICATION_TYPES.ADD_ROUTINE_ASSIGNEE:
          return {
            color: 'teal darken-1',
            icon: 'mdi-clipboard-account-outline',
          }
        case NOTIFICATION_TYPES.RECOGNITION_RECEIVED:
          return {
            color: 'yellow',
            icon: 'mdi-medal-outline',
          }
        case NOTIFICATION_TYPES.VALIDATION_REQUEST_ASSIGNED:
          return {
            color: 'blue',
            icon: 'mdi-check-decagram-outline',
          }
        case NOTIFICATION_TYPES.VALIDATION_REQUEST_REJECTED:
          return {
            color: 'red',
            icon: 'mdi-alert-decagram-outline',
          }
        case NOTIFICATION_TYPES.REPORT_GENERATED:
          return {
            color: 'green',
            icon: 'mdi-clipboard-check-outline',
          }
        case NOTIFICATION_TYPES.REV_STATUS_CHANGE:
          return {
            color: 'blue',
            icon: 'mdi-progress-star',
          }
        case NOTIFICATION_TYPES.REV_COMPLETED_WITHOUT_COACH:
          return {
            color: 'orange',
            icon: 'mdi-account-alert-outline',
          }
        case NOTIFICATION_TYPES.REV_COMPLETED_WITH_COACH:
          return {
            color: 'blue-grey darken-1',
            icon: 'mdi-rocket-launch-outline',
          }
        case NOTIFICATION_TYPES.CHALLENGE_COMMENT_CREATED:
          return {
            color: 'green',
            icon: 'mdi-comment-outline',
          }
        case NOTIFICATION_TYPES.CHALLENGE_LINKED:
          return {
            color: 'blue',
            icon: 'mdi-plus-circle-outline',
          }
        case NOTIFICATION_TYPES.MEMBER_ADDED_TO_TEAM:
          return {
            color: 'orange',
            icon: 'mdi-account-multiple-check-outline',
          }
        case NOTIFICATION_TYPES.TASK_ASSIGNED:
          return {
            color: 'green lighten-1',
            icon: 'mdi-check',
          }
        case NOTIFICATION_TYPES.TASK_COMPLETED:
          return {
            color: 'green',
            icon: 'mdi-check-all',
          }
        case NOTIFICATION_TYPES.BOARD_SHARED:
          return {
            color: 'teal darken-1',
            icon: 'mdi-view-column',
          }
        case NOTIFICATION_TYPES.TASKS_REMINDER:
          return {
            color: 'yellow',
            icon: 'mdi-order-bool-ascending-variant',
          }
        case NOTIFICATION_TYPES.TASK_STATUS_CHANGED:
          return {
            color: this.taskStatusColor,
            icon:
              this.notification?.metadata?.status === 'completed'
                ? 'mdi-check-all'
                : 'mdi-progress-pencil',
          }
        case NOTIFICATION_TYPES.TASK_DUE_DATE_CHANGED:
          return {
            color: 'teal darken-1',
            icon: 'mdi-calendar-outline',
          }
        case NOTIFICATION_TYPES.TASK_ATTACHMENT_ADDED:
          return {
            color: 'teal darken-1',
            icon: 'mdi-paperclip',
          }
        case NOTIFICATION_TYPES.ROUTINE_DEFINITION_REMINDER:
          return {
            color: 'teal',
            icon: 'mdi-clipboard-text-play-outline',
          }
        case NOTIFICATION_TYPES.REVS_PENDING_TO_COMPLETE_REMINDER:
          return {
            color: 'blue darken-1',
            icon: 'mdi-progress-helper',
          }
        case NOTIFICATION_TYPES.REVS_PENDING_TO_IMPLEMENT_REMINDER:
          return {
            color: 'orange lighten-1',
            icon: 'mdi-rocket-outline',
          }
        case NOTIFICATION_TYPES.REVS_PENDING_TO_REQUEST_VALIDATION_REMINDER:
          return {
            color: 'orange',
            icon: 'mdi-rocket-launch-outline',
          }
        case NOTIFICATION_TYPES.REVS_PENDING_TO_VALIDATE_REMINDER:
          return {
            color: 'yellow',
            icon: 'mdi-alert-decagram-outline',
          }
        case NOTIFICATION_TYPES.REVS_ASSIGNED_PENDING_TO_VALIDATE_REMINDER:
          return {
            color: 'yellow',
            icon: 'mdi-alert-decagram-outline',
          }
        case NOTIFICATION_TYPES.ROUTINE_COLLABORATOR_ADDED:
          return {
            color: 'yellow',
            icon: 'mdi-clipboard-edit-outline',
          }
        case NOTIFICATION_TYPES.ROUTINE_COLLABORATOR_REMOVED:
          return {
            color: 'red',
            icon: 'mdi-clipboard-alert-outline',
          }
        case NOTIFICATION_TYPES.AI_TOKENS_LIMIT_100_THRESHOLD_REACHED:
        case NOTIFICATION_TYPES.AI_TOKENS_LIMIT_90_THRESHOLD_REACHED:
        case NOTIFICATION_TYPES.AI_TOKENS_LIMIT_80_THRESHOLD_REACHED:
        case NOTIFICATION_TYPES.AI_TOKENS_LIMIT_70_THRESHOLD_REACHED:
        case NOTIFICATION_TYPES.USER_AI_TOKENS_EXHAUSTED:
          return {
            color: 'blue darken-2',
            icon: 'mdi-creation-outline',
          }
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_REQUESTED:
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_DAILY_REMINDER:
          return {
            color: 'blue',
            icon: 'mdi-check-decagram-outline',
          }
        case NOTIFICATION_TYPES.ROUTINE_APPROVED:
          return {
            color: 'green lighten-1',
            icon: 'mdi-checkbox-marked-circle-outline',
          }
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_REJECTED:
          return {
            color: 'red',
            icon: 'mdi-alert-decagram-outline',
          }
        case NOTIFICATION_TYPES.REV_REPLICATED:
          return {
            color: 'blue',
            icon: 'mdi-content-duplicate',
          }
        case NOTIFICATION_TYPES.SECONDARY_INVITATION:
          return {
            color: 'primary',
            icon: 'mdi-domain',
          }
        default:
          return {
            color: 'blue',
            icon: 'mdi-bell-outline',
          }
      }
    },
  },

  methods: {
    onClick(notification) {
      this.$emit('click', notification)
    },
  },
}
</script>
<style lang="scss">
.v-list-item--link:hover {
  background-color: $color-blue-grey-lighten3;
}
</style>
